import React, { useState, useMemo, useCallback } from 'react';
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronRight, Code2 } from 'lucide-react';
import type { QueryResultSet } from '../../../../types/queryExecution';
import { ExpandedValueDialog } from '../../table-explorer/components/ExpandedValueDialog';
import { detectContentMode, type ExpandedEditMode } from '../../table-explorer/utils/expandedEditDetection';
import type { JsonClassification } from '../../../sql-resolver/types';
import {
  guessJsonColumn,
  isExplicitlyNotJsonColumn,
  isJsonClassifiedColumn,
  isStringDataType,
  looksLikeJsonString,
} from '../utils/jsonDetection';
import { formatRowCount } from '../utils/formatRowCount';
import { isNumericColumn, shouldGroupNumericColumn, formatNumericDisplay } from '../utils/columnAlignment';
import { useAppStore } from '../../../../store/appStore';
import { CELL_EXPAND_BUTTON_CLASS, CELL_EXPAND_WRAPPER_CLASS } from './cells/cellExpandButton';

interface StackedResultsViewProps {
  resultSets: QueryResultSet[];
  /**
   * Optional content rendered at the top of the stacked list — used for the
   * collapsible Output preview when the script contained user PRINTs.
   */
  topSlot?: React.ReactNode;
}

const MIN_COLUMN_WIDTH = 50;
const MAX_AUTO_COLUMN_WIDTH = 400;

/** Renders a single result set table (non-virtualized) */
function ResultSetTable({
  resultSet,
  index,
  onOpenJson,
}: {
  resultSet: QueryResultSet;
  index: number;
  onOpenJson: (title: string, json: string, dataType?: string, jsonClassification?: JsonClassification) => void;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const columnHelper = useMemo(() => createColumnHelper<Record<string, unknown>>(), []);

  const normalizedColumns = useMemo(() => {
    const cols = resultSet.columns ?? [];
    const sourceTables = new Set<string>();
    for (const col of cols) {
      if (!col.sourceTable) continue;
      sourceTables.add(col.sourceTable);
    }

    const showSourceTables = sourceTables.size > 1;

    return cols.map((col, colIndex) => ({
      id: col.id || col.name || `col_${colIndex}`,
      key: col.id ?? col.name, // Key for row data access — see ResultsGrid's normalizedColumns
      name: col.name || `Column ${colIndex + 1}`,
      dataType: col.dataType,
      isPrimaryKey: col.isPrimaryKey,
      isForeignKey: col.isForeignKey,
      sourceTable: col.sourceTable,
      showSourceTable: showSourceTables && Boolean(col.sourceTable),
      jsonClassification: col.jsonClassification,
    }));
  }, [resultSet.columns]);

  const resultRows = Array.isArray(resultSet.rows) ? resultSet.rows : [];

  const jsonColumnIdSet = useMemo(() => {
    const jsonColumns = new Set<string>();

    for (const column of normalizedColumns) {
      // E1 column-metadata path: explicit JSON classification wins.
      if (isJsonClassifiedColumn(column.jsonClassification)) {
        jsonColumns.add(column.id);
        continue;
      }
      // Explicit `not-json` suppresses the heuristic auto-format.
      if (isExplicitlyNotJsonColumn(column.jsonClassification)) continue;

      const shouldCheckByType = isStringDataType(column.dataType);
      const shouldCheckUnknown = !column.dataType;

      if (!shouldCheckByType && !shouldCheckUnknown) continue;
      if (guessJsonColumn(resultRows, column.key, { maxParseAttempts: 8, minSuccessfulParses: 2 })) {
        jsonColumns.add(column.id);
      }
    }

    return jsonColumns;
  }, [normalizedColumns, resultRows]);

  // PP-01 — numeric alignment sets, identical to the pair `ResultsGrid` and
  // `TableExplorerGrid` build from the SAME shared `columnAlignment` helpers.
  // Stack mode is a Query-Editor-family surface rendering the very same result
  // set as tabs mode, so a number that groups in one must group in the other;
  // before this it did not, and toggling tabs -> stack visibly un-grouped every
  // value on screen. Display only — the `title` tooltip and every copy path
  // below keep the raw value. The `numericGrouping` mode is read once here
  // (`off` empties the grouped set) — same policy as the two grids.
  const numericGrouping = useAppStore((state) => state.numericGrouping);
  const { numericColumnIds, groupedNumericColumnIds } = useMemo(() => {
    const numeric = new Set<string>();
    const grouped = new Set<string>();
    for (const column of normalizedColumns) {
      if (!isNumericColumn(column)) continue;
      numeric.add(column.id);
      if (numericGrouping !== 'off' && shouldGroupNumericColumn(column)) grouped.add(column.id);
    }
    return { numericColumnIds: numeric, groupedNumericColumnIds: grouped };
  }, [normalizedColumns, numericGrouping]);

  const columnDefs = useMemo<ColumnDef<Record<string, unknown>>[]>(() =>
    normalizedColumns.map((column) => {
      const isNumeric = numericColumnIds.has(column.id);
      const isGroupedNumeric = groupedNumericColumnIds.has(column.id);
      return columnHelper.accessor((row) => row[column.key], {
        id: column.id,
        header: () => (
          <div className={`relative flex flex-col leading-tight ${isNumeric ? 'items-end' : ''} ${column.showSourceTable && column.sourceTable ? 'pt-3' : ''}`}>
            {column.showSourceTable && column.sourceTable && (
              <span
                className={`absolute top-0 left-0 right-0 text-[9px] leading-none text-muted-foreground truncate ${isNumeric ? 'text-right' : ''}`}
                title={column.sourceTable}
              >
                {column.sourceTable}
              </span>
            )}
            <span className="flex items-center gap-1">
              {column.isPrimaryKey && (
                <span className="text-amber-700 dark:text-amber-400 text-[10px]" title="Primary Key">PK</span>
              )}
              {column.isForeignKey && (
                <span className="text-blue-500 text-[10px]" title="Foreign Key">FK</span>
              )}
              <span>{column.name}</span>
            </span>
          </div>
        ),
        minSize: MIN_COLUMN_WIDTH,
        cell: ({ getValue }) => {
          const value = getValue();
          if (value === null || value === undefined) {
            return <span className="text-muted-foreground/60 italic text-xs">NULL</span>;
          }
          if (typeof value === 'object') {
            return <span className="font-mono text-xs">{JSON.stringify(value)}</span>;
          }
          const rawDisplayValue = String(value);
          // PP-01 — grouping is DISPLAY ONLY. The `<td>`'s `title` below and
          // any native text selection of this cell are the only ways a value
          // leaves this view (stack mode has no Ctrl+C handler, no context
          // menu and no export menu of its own), and the tooltip keeps the raw
          // string deliberately.
          const displayValue = isGroupedNumeric
            ? formatNumericDisplay(value, numericGrouping) ?? rawDisplayValue
            : rawDisplayValue;

          if (typeof value === 'string' && jsonColumnIdSet.has(column.id) && looksLikeJsonString(value)) {
            return (
              // PP-10 — the shared hover-only expand affordance. This used to be
              // the retired always-visible pattern, the last copy of it left in
              // the app after T4.5 consolidated both grids onto this module.
              <span className={CELL_EXPAND_WRAPPER_CLASS}>
                <button
                  type="button"
                  className={CELL_EXPAND_BUTTON_CLASS}
                  title="View JSON"
                  aria-label="View JSON"
                  data-testid="stacked-cell-expand-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenJson(column.name, value, column.dataType, column.jsonClassification);
                  }}
                >
                  <Code2 className="h-3 w-3" />
                </button>
                <span className="truncate min-w-0">{displayValue}</span>
              </span>
            );
          }

          // E1 value-shape fallback: explicit `not-json` metadata + JSON-shaped
          // value → "Looks like JSON" tooltip, no auto-format.
          if (
            typeof value === 'string' &&
            isExplicitlyNotJsonColumn(column.jsonClassification) &&
            looksLikeJsonString(value)
          ) {
            return <span title="Looks like JSON">{displayValue}</span>;
          }

          return displayValue;
        },
      });
    }),
    [columnHelper, jsonColumnIdSet, normalizedColumns, onOpenJson, numericColumnIds, groupedNumericColumnIds, numericGrouping]
  );

  const safeResultRows = Array.isArray(resultSet.rows) ? resultSet.rows : [];

  const table = useReactTable({
    data: safeResultRows,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_row, rowIndex) => String(rowIndex),
  });

  const rowCount = resultSet.rowCount ?? safeResultRows.length;

  return (
    <div className="border border-border rounded-md overflow-hidden" data-testid={`stacked-result-${index}`}>
      {/* Collapsible header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/50 hover:bg-muted/70 transition-colors text-left"
        data-testid={`stacked-result-header-${index}`}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">
          Result {index + 1}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          ({formatRowCount(rowCount)})
        </span>
      </button>

      {/* Table content - only render when expanded */}
      {!isCollapsed && (
        <div className="overflow-x-auto" data-testid={`stacked-result-content-${index}`}>
          <table className="border-collapse text-sm w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`px-3 py-1.5 ${numericColumnIds.has(header.id) ? 'text-right' : 'text-left'} font-medium text-xs
                                 bg-muted border-b border-r border-border last:border-r-0
                                 text-muted-foreground whitespace-nowrap select-none overflow-hidden text-ellipsis`}
                      style={{ minWidth: MIN_COLUMN_WIDTH, maxWidth: MAX_AUTO_COLUMN_WIDTH }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                  {/* PP-05 — inert filler carrying the header band to the right
                      edge, mirroring `GridHeaderRow`'s. Deliberately NOT a
                      TanStack column: `getVisibleCells()` never yields it, it
                      carries no `data-column-id`, no handlers and no tab stop,
                      so nothing structural can pick it up.

                      `width: 100%` is what makes the slack land here. Under
                      `table-layout: auto` the `maxWidth` on the data cells is
                      inert, so without a greedy filler the columns stretched to
                      absorb the pane (measured: 433px / 618px on a 2-column
                      result in a 1051px pane). A cell asking for the whole table
                      width is served last, after every other column has taken
                      its content width. */}
                  <th
                    aria-hidden="true"
                    data-grid-filler="true"
                    data-testid={`stacked-grid-filler-header-${index}`}
                    className="p-0 bg-muted border-b border-border select-none"
                    style={{ width: '100%' }}
                  />
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  className={`
                    border-b border-border/50 last:border-b-0
                    hover:bg-selection transition-colors
                    ${rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  `}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      // `group/cell` scopes the PP-10 hover reveal of the expand
                      // button to this cell, exactly as both grids do.
                      className={`px-3 py-1 border-r border-grid-rule last:border-r-0 group/cell
                                 whitespace-nowrap text-foreground overflow-hidden text-ellipsis
                                 ${numericColumnIds.has(cell.column.id) ? 'text-right' : ''}`}
                      style={{ minWidth: MIN_COLUMN_WIDTH, maxWidth: MAX_AUTO_COLUMN_WIDTH }}
                      title={(() => {
                        const value = cell.getValue();
                        if (value === null || value === undefined) return 'NULL';
                        if (typeof value === 'object') return JSON.stringify(value);
                        return String(value);
                      })()}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  {/* PP-05 filler — inert: no handlers, not a TanStack column.
                      `width: 100%` matches the header filler so the column stays
                      greedy in auto layout (see the header filler above).
                      `select-none` keeps it out of a native text selection, so a
                      user selecting and copying a row gets no trailing tab. */}
                  <td
                    aria-hidden="true"
                    data-grid-filler="true"
                    className="p-0 border-0 select-none"
                    style={{ width: '100%' }}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const StackedResultsView = React.memo(function StackedResultsView({
  resultSets,
  topSlot,
}: StackedResultsViewProps) {
  const [expandedDialog, setExpandedDialog] = useState<{
    title: string; value: string; mode: ExpandedEditMode;
  } | null>(null);

  const openExpandedDialog = useCallback(
    (title: string, json: string, dataType?: string, jsonClassification?: JsonClassification) => {
      const mode = detectContentMode(json, { dataType, jsonClassification });
      setExpandedDialog({
        title,
        value: json,
        mode,
      });
    },
    [],
  );

  return (
    <div className="flex-1 min-h-0 overflow-auto" data-testid="stacked-results-view">
      <div className="flex flex-col gap-3 p-3">
        {topSlot}
        {resultSets.map((resultSet, index) => (
          <ResultSetTable key={index} resultSet={resultSet} index={index} onOpenJson={openExpandedDialog} />
        ))}
      </div>

      <ExpandedValueDialog
        open={expandedDialog !== null}
        onOpenChange={(open) => {
          if (!open) setExpandedDialog(null);
        }}
        title={expandedDialog?.title ?? 'JSON'}
        initialValue={expandedDialog?.value ?? ''}
        initialMode={expandedDialog?.mode ?? 'json'}
        readOnly
        onConfirm={() => setExpandedDialog(null)}
      />
    </div>
  );
});
